import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { acaConfig } from 'aca-config';
import { MondoFormControl } from 'app/core/mondo-form-builder';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent implements OnChanges {
  @Input() dateOptions: DateOptions;
  @Input() fControl: MondoFormControl<Date>;

  @ViewChild('dateInputField', { static: false }) dateInputField: ElementRef;
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;

  displayDate: MondoFormControl<string> = new MondoFormControl('');
  date: Date | null = null;
  timeValue: string | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fControl && this.fControl) {
      setTimeout(() => this.initFromControl(), 0);
    }
  }

  private initFromControl() {
    if (!this.fControl) return;

    const val = this.fControl.value;
    this.date = val ? new Date(val) : null;

    if (this.dateOptions.timePickerLabel && this.date) {
      this.timeValue = this.extractTime(this.date);
    } else {
      this.timeValue = null;
    }

    this.updateInputField(this.date);
  }

  private extractTime(date: Date): string {
    const h = ('0' + date.getHours()).slice(-2);
    const m = ('0' + date.getMinutes()).slice(-2);
    return `${h}:${m}`;
  }

  onTimeChanged() {
    if (!this.date) {
      this.date = new Date();
    }

    if (this.timeValue) {
      const [hours, minutes] = this.timeValue.split(':').map(Number);
      this.date.setHours(hours || 0, minutes || 0, 0, 0);
    }
    this.fControl.setValue(this.date);
    this.updateInputField(this.date);
  }

  dateChanged(date) {
    this.checkAndUpdate(date, PickerMode.day);
  }

  monthSelected(date) {
    this.checkAndUpdate(date, PickerMode.month);
  }

  yearSelected(date) {
    this.checkAndUpdate(date, PickerMode.year);
  }

  checkAndUpdate(date: Date, pickerMode?: PickerMode) {
    this.date = new Date(date);

    if (this.dateOptions.timePickerLabel && this.timeValue) {
      const [hours, minutes] = this.timeValue.split(':').map(Number);
      this.date.setHours(hours || 0, minutes || 0, 0, 0);
    }

    if (pickerMode === this.dateOptions.pickerMode) {
      this.updateInputField(this.date);
      this.fControl.setValue(this.date);
      this.picker.close();
    }
  }

  updateInputField(date: Date) {
    if (!this.dateInputField) return;
    if (!date) {
      this.dateInputField.nativeElement.value = '';
      return;
    }

    let text = this.getDisplayText(date);
    if (this.dateOptions.timePickerLabel && this.timeValue) {
      text += ` ${this.timeValue}`;
    }

    this.dateInputField.nativeElement.value = text;
    this.cdr.markForCheck();
  }

  getDisplayText(date: Date) {
    switch (this.dateOptions.pickerMode) {
      case PickerMode.day:
        return date.toLocaleDateString(acaConfig.dateFormat, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      case PickerMode.month:
        return date.toLocaleDateString(acaConfig.dateFormat, {
          year: 'numeric',
          month: 'short',
        });
      case PickerMode.year:
        return date.toLocaleDateString(acaConfig.dateFormat, {
          year: 'numeric',
        });
    }
  }

  openPicker() {
    this.picker.open();
  }

  pickerOpened() {
    switch (this.dateOptions.pickerMode) {
      case PickerMode.day:
        break;
      case PickerMode.month:
      case PickerMode.year:
        break;
    }
  }

  getBtnClass() {
    switch (this.dateOptions.pickerMode) {
      case PickerMode.day:
        return '';
      case PickerMode.month:
      case PickerMode.year:
        return 'hide-btn';
    }
  }

  clear() {
    this.fControl.reset();
    this.date = null;
    this.timeValue = null;
    this.displayDate.reset();
    this.updateInputField(null);
  }
}

export interface DateOptions {
  minDate?: Date;
  maxDate?: Date;
  startDate: Date;
  pickerMode: PickerMode;
  disabled: boolean;
  placeholder: string;
  timePickerLabel: string;
  optionalHint: string;
  startView: 'month' | 'year' | 'multi-year';
  showClearBtn?: boolean;
  value?: Date;
}

export enum PickerMode {
  day,
  month,
  year,
}
